import { API_URL, LoginResponse, User } from '@/lib/types'

const AUTH_COOKIE_NAME = 'ses-auth-token'
const USER_COOKIE_NAME = 'ses-user'

function setCookie(name: string, value: string, days: number = 7): void {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${secure}`
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
}

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    const cookie = parts.pop()?.split(';').shift()
    return cookie ? decodeURIComponent(cookie) : null
  }
  return null
}

export interface LoginResult {
  success: boolean
  user?: User
  error?: string
}

export async function login(username: string, password: string): Promise<LoginResult> {
  try {
    const res = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ login: username, password }),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Login failed' }))
      return { success: false, error: error.error || 'Invalid credentials' }
    }

    const data: LoginResponse = await res.json()

    // Decode JWT to get user info (basic decoding, not verification)
    const payload = JSON.parse(atob(data.token.split('.')[1]))
    const role = payload.role || 'user'

    // Set cookies
    setCookie(AUTH_COOKIE_NAME, data.token)
    setCookie(USER_COOKIE_NAME, JSON.stringify({ username, role }))

    return { success: true, user: { username, role } }
  } catch (error) {
    console.error('Login error:', error)
    return { success: false, error: 'Network error. Please try again.' }
  }
}

export function logout(): void {
  deleteCookie(AUTH_COOKIE_NAME)
  deleteCookie(USER_COOKIE_NAME)
}

export function getStoredUser(): User | null {
  const userCookie = getCookie(USER_COOKIE_NAME)
  if (!userCookie) return null

  try {
    return JSON.parse(userCookie)
  } catch {
    return null
  }
}

export function isAuthenticated(): boolean {
  return !!getCookie(AUTH_COOKIE_NAME)
}
